/**
 * A founder's people and how far each one's setup has come (board onboarding-r2, answer 6). One reading for the
 * founder's dashboard and the admin founder page. Pure.
 */
import type { LightLook as LightState } from './tones'
import type { Access, PersonView } from './schemas'

/** The two ways onto the founder's computer, one line each, as the invite form and the lists say them. */
export const ACCESS_CHOICES: { value: Access; label: string; line: string; adminLabel: string; adminLine: string }[] = [
  {
    value: 'shared',
    label: 'Share your account',
    line: "They work as you on your computer: same agent sign-in and paired devices, so you can work together and see each other's work.",
    adminLabel: "Founder's user",
    adminLine: "Shares the founder's Linux user, agent sign-in and Paseo daemon (the default).",
  },
  {
    value: 'own',
    label: 'Their own account',
    line: 'A clean account of their own on your computer: their own agent sign-in, devices and copy of the code.',
    adminLabel: 'Own Linux user',
    adminLine: 'A Linux user of their own on the box: their own Paseo daemon, Claude Code, clone and agent sign-in.',
  },
]

/** Who reads the list: the words for each account depend on it. */
export type Reader = 'founder' | 'teammate' | 'admin'

/** The Computer column, as the reader would say it. */
export function accessWords(p: Pick<PersonView, 'role'> & { access: Access }, reader: Reader, mine = false): string {
  if (reader === 'admin') return p.access === 'shared' ? "founder's user" : 'own user'
  if (p.access === 'own') return mine ? 'your own account' : 'own account'
  if (p.role === 'founder') return mine ? 'your account' : reader === 'founder' ? 'your account' : "the founder's account"
  if (mine) return "shares the founder's account"
  return reader === 'founder' ? 'shares your account' : "shares the founder's account"
}

/** One stage of a person's setup, as a light. */
export interface Stage {
  key: 'account' | 'paseo' | 'code' | 'invite' | 'signin' | 'setup'
  label: string
  light: LightState
}

export interface Progress {
  /** One line on where they are. */
  words: string
  light: LightState
  stages: Stage[]
  /** Stages done, of those that apply. */
  done: number
  total: number
}

const STEP_LIGHT: Record<string, LightState> = { done: 'ok', skipped: 'ok', manual: 'look', running: 'work', pending: 'off', failed: 'down' }

/** A step of their Add person run as a light; `null` when the run doesn't have it (an older run). */
function stepLight(p: PersonView, key: string): LightState | null {
  const s = p.run?.steps.find((x) => x.key === key)
  return s ? (STEP_LIGHT[s.status] ?? 'off') : null
}

const BUILDS = new Set(['founder', 'teammate'])

/** Where a person is, from their account, their latest Add person run, their first sign-in and their person setup. */
export function personProgress(p: PersonView, opts: { admin?: boolean } = {}): Progress {
  const builds = BUILDS.has(p.role)
  const hasAccount = p.access !== null
  const stages: Stage[] = []
  if (builds) {
    stages.push({ key: 'account', label: p.access === 'own' ? 'Their account' : 'Account', light: hasAccount ? 'ok' : (stepLight(p, 'linuxUser') ?? 'off') })
    // Runs from before Add person was automated left these steps manual; an account recorded since says they're set.
    const paseo = stepLight(p, 'paseoDaemon')
    stages.push({ key: 'paseo', label: 'Paseo', light: hasAccount && (paseo === null || paseo === 'look') ? 'ok' : (paseo ?? 'off') })
    if (p.access === 'own') stages.push({ key: 'code', label: 'Code', light: stepLight(p, 'personClone') ?? 'off' })
  }
  // Being listed is the invite: only a step still to run or one that failed says otherwise.
  const invite = stepLight(p, 'kindeInvite')
  stages.push({ key: 'invite', label: 'Invited', light: invite === null || invite === 'look' ? 'ok' : invite })
  stages.push({ key: 'signin', label: 'Signed in', light: p.signedIn ? 'ok' : 'off' })
  if (builds) stages.push({ key: 'setup', label: 'Setup', light: p.setupCompletedAt ? 'ok' : p.signedIn ? 'work' : 'off' })

  const done = stages.filter((s) => s.light === 'ok').length
  const at = { done, total: stages.length, stages }
  const failed = p.run?.status === 'failed'
  const running = p.run?.status === 'running' || p.run?.status === 'pending'

  if (failed) {
    const step = p.run!.steps.find((s) => s.status === 'failed')
    return { ...at, light: 'down', words: opts.admin ? `Add person stopped at ${step?.name ?? 'a step'}` : "Their setup stopped. We're on it." }
  }
  if (running) return { ...at, light: 'work', words: p.access === 'own' || p.run?.steps.some((s) => s.key === 'linuxUser' && s.status === 'running') ? 'Making their account on your computer…' : 'Setting them up…' }
  if (!builds) return { ...at, light: p.signedIn ? 'ok' : 'off', words: p.signedIn ? 'Views the screens you share' : 'Invited, not signed in yet' }
  if (!hasAccount) return { ...at, light: 'look', words: 'No account on the computer yet' }
  if (!p.signedIn) return { ...at, light: 'off', words: 'Invited, not signed in yet' }
  if (!p.setupCompletedAt) return { ...at, light: 'work', words: 'Signed in, setting up' }
  return { ...at, light: 'ok', words: 'All set' }
}

/** Plain checks on the invite form, before the API's. */
export function inviteProblem(email: string, people: PersonView[]): string | null {
  const e = email.trim().toLowerCase()
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return 'Enter their email address.'
  const there = people.find((p) => p.email === e)
  if (there && there.access === 'own') return `${e} already has their own account.`
  return null
}
